/**
 * Paste-into-Projects/MiniApps — lets the drive clipboard (Ctrl+C in DriveView,
 * WorkspaceExplorer, etc.; persisted in localStorage) be pasted while ON the
 * Projects or MiniApps page. Destination is the caller's own projects container
 * (`GET /api/workspaces/container` — the same placement "New project" uses), so
 * a `.project`/`.miniapp` folder copied from anywhere in the drive lands as a
 * card on these pages.
 *
 * Conflicts never prompt here: the destination is a container the user isn't
 * looking at, so "make a copy" intent is unambiguous — always keepBoth (the
 * server dedupes to `Foo (1).project`, keeping the package ext).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { moveErrorText } from '../moveErrorText'
import { withMoveProgress } from '../moveProgress'
import { useQueryClient } from '@tanstack/react-query'
import { isProjectFolder, isMiniappFolder } from '@papan/types'
import { request, getActiveTenantId, pinnedBatch } from '@papan/api-client'
import { useToast, useConfirm } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { filesApi } from '@papan/files/client'
import { pasteItems } from '@papan/files/client/paste'
import type { ProjectCardVariant } from './ProjectCard'
import { projectKeys } from './data'
import { confirmCrossTenantPaste, resolveDriveLabel } from '../crossTenantPaste'
import { clearSpentCut } from '../spentCut'

const CLIPBOARD_KEY = 'papan-clipboard'

/** Shape DriveView/WorkspaceExplorer persist under `papan-clipboard`. */
interface ClipboardData {
  items: Array<{ id: string; name: string; isFolder: boolean; parentId: string | null }>
  isCut: boolean
  tenantId: string
  orgEmail?: string
  /** (#752) The source drive's label, captured at copy time. */
  sourceLabel?: string
}

function readClipboard(): ClipboardData | null {
  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ClipboardData>
    // Shape-check before trusting it: `eligible` is derived in a useMemo during
    // RENDER, so a malformed entry (older format, foreign writer, hand-edited
    // storage) would throw `items.filter is not a function` inside the render
    // pass — taking the whole page down via the ErrorBoundary, and staying down
    // across reloads until localStorage is cleared.
    if (!parsed || !Array.isArray(parsed.items)) return null
    // (#752) Unattributable (unstamped legacy) or another tenant's CUT: not
    // pasteable here — don't show a Paste that can only refuse or do nothing.
    const active = getActiveTenantId()
    if (!parsed.tenantId) return null
    if (parsed.isCut && active && parsed.tenantId !== active) return null
    return parsed as ClipboardData
  } catch {
    return null
  }
}

export function usePasteProjects(variant: ProjectCardVariant, dest?: { isOrg?: boolean; tenantName?: string; deckActive?: boolean }) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const [clip, setClip] = useState<ClipboardData | null>(() => readClipboard())
  const [pasting, setPasting] = useState(false)
  /** Synchronous re-entrancy guard — `pasting` drives the UI, this stops the
   *  second invocation before React has committed the first. */
  const pastingRef = useRef(false)

  // The MiniApps page only lists `.miniapp` folders, so only those are
  // pastable there; the Projects page lists both kinds.
  const filterEligible = useCallback(
    (items: ClipboardData['items'] | undefined) =>
      (items ?? []).filter(i => i.isFolder &&
        (variant === 'miniapp' ? isMiniappFolder(i.name) : (isProjectFolder(i.name) || isMiniappFolder(i.name)))),
    [variant],
  )

  // Cross-tab sync via the storage event; same-tab copies re-read on focus
  // (navigating Drive → Projects remounts this hook anyway).
  useEffect(() => {
    const sync = () => setClip(readClipboard())
    const onStorage = (e: StorageEvent) => { if (e.key === CLIPBOARD_KEY) sync() }
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', sync)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', sync)
    }
  }, [])

  const eligible = useMemo(() => filterEligible(clip?.items), [clip, filterEligible])

  const paste = useCallback(async () => {
    const c = readClipboard()
    const items = filterEligible(c?.items)
    // Ref, not the `pasting` state: `setPasting(true)` only takes effect after a
    // commit, so a held Ctrl+V (key auto-repeat) or a fast double-press re-ran
    // this with the pre-commit closure still reading `false` — landing TWO
    // keepBoth copies from one gesture. The ref flips synchronously.
    if (!c || items.length === 0 || pastingRef.current) return
    pastingRef.current = true
    setPasting(true)
    // #1316 review round 2 (owner, 2026-10-28): the paste the person started FINISHES as the identity that started it —
    // every request is sent as it (`batch.opts()`), never as the next session; what it SHOWS stops with its session
    const batch = pinnedBatch()
    const shown = () => !batch.ended()
    try {
      const { folderId: destId } = await request<{ folderId: string }>('/api/workspaces/container', batch.opts())
      // the tenant the paste STARTED in — a switch during the container round trip must not move the comparison (#1316 r3)
      const tenant = batch.identity.tenantId
      const crossTenant = !!c.tenantId && !!tenant && c.tenantId !== tenant

      // Cross-tenant CUT is refused for everything, mounts included — checked
      // before any write, since a share mount can't be moved across a tenant
      // boundary either and there's no cross-tenant copy path for one.
      if (c.isCut && crossTenant) {
        toast.error(t('driveView.toast.crossTenantMove', {
          defaultValue: 'Items can’t be moved from one of your drives to another. Copy them instead — moving into or out of a shared folder still works.',
        }))
        return
      }
      // (#752) Mount ids resolve only in the tenant that received the share.
      if (crossTenant && items.some(i => i.id.startsWith('s_'))) {
        toast.error(t('driveView.crossTenantPaste.mountRefused'))
        return
      }
      // (#752) A cross-tenant copy moves bytes across an org boundary — ask
      // first, naming both drives, same as the drive's own paste.
      if (crossTenant) {
        const from = c.sourceLabel ?? await resolveDriveLabel(t, c.tenantId, undefined, undefined)
        const to = await resolveDriveLabel(t, tenant, dest?.isOrg, dest?.tenantName)
        // never ASK in the session after the paste's own: nothing confirmed, nothing pasted
        if (!shown() || getActiveTenantId() !== tenant) return
        if (!(await confirmCrossTenantPaste(confirm, t, { count: items.length, from, to }))) return
      }

      // Share-mount rows (s_…) can't go through /copy — route them through
      // move, which the server resolves to a copy that preserves the original
      // (same routing as DriveView's paste).
      const mountItems = items.filter(i => i.id.startsWith('s_'))
      const normalItems = items.filter(i => !i.id.startsWith('s_'))
      const doneNames: string[] = []
      let firstError = ''
      const isMove = c.isCut && !crossTenant

      for (const item of mountItems) {
        // Copy → Paste COPIES out of a received share (#753); only a Cut moves it (#982 — every
        // move says `move: true`, so a copy must say `copy`).
        try { await withMoveProgress(toast, t('driveView.toast.moving', { name: item.name }), (hooks) => filesApi.moveFile(item.id, { parentId: destId, ...(c.isCut ? {} : { copy: true }) }, { ...hooks, ...batch.opts() }), batch.ended); doneNames.push(item.name) }
        catch (err) { firstError ||= moveErrorText(err, t) }
      }

      if (normalItems.length > 0) {
        if (c.isCut) {
          for (const item of normalItems) {
            try { await withMoveProgress(toast, t('driveView.toast.moving', { name: item.name }), (hooks) => filesApi.moveFile(item.id, { parentId: destId }, { ...hooks, ...batch.opts() }), batch.ended); doneNames.push(item.name) }
            // #801 E1: a project is never split — refused whole (409 `entityRoot`).
            catch (err) { firstError ||= `${item.name}: ${moveErrorText(err, t)}` }
          }
        } else {
          const { copied, errors } = await pasteItems(
            normalItems, destId, 'copy',
            async () => ({ resolution: 'keepBoth' as const, applyToAll: true }),
            { batch, ...(crossTenant ? { crossTenant: { sourceTenantId: c.tenantId, sourceOrgEmail: c.orgEmail } } : {}) },
          )
          doneNames.push(...copied.map(x => x.name))
          if (errors.length > 0) {
            // Name the failing item — a bare server message ("Read-only
            // permission required") is undiagnosable without knowing which
            // clipboard entry it was about.
            const nameById = new Map(normalItems.map(i => [i.id, i.name]))
            firstError ||= `${nameById.get(errors[0].sourceId) ?? errors[0].sourceId}: ${errors[0].message}`
          }
        }
      }

      // A cut is consumed once EVERY item moved — placed after both loops and
      // gated on there being no failure, so a partial cut keeps the clipboard
      // armed. Clearing it early (or unconditionally) stranded the user: the
      // error toast fired and the items they cut were no longer on the
      // clipboard to retry with. Covers the mount-only cut too, which used to
      // miss the clear entirely.
      // Only while the clipboard still holds THIS cut (#1316 review round 2): the next session may have cut or copied
      // something else meanwhile.
      if (c.isCut && !firstError && doneNames.length > 0 && clearSpentCut(c)) setClip(null)
      // the batch finished as its starter — nothing of it is shown in the session after its own
      if (!shown()) return

      if (doneNames.length > 0) {
        const name = doneNames[0]
        const more = doneNames.length - 1
        toast.success(more > 0
          ? t(isMove ? 'driveView.toast.moved' : 'driveView.toast.copied', { name, count: more, defaultValue: `Copied “${name}” and ${more} ${more === 1 ? 'other' : 'others'}` })
          : t(isMove ? 'driveView.toast.movedOne' : 'driveView.toast.copiedOne', { name, defaultValue: `Copied “${name}”` }))
        queryClient.invalidateQueries({ queryKey: projectKeys.list() })
        queryClient.invalidateQueries({ queryKey: projectKeys.recent() })
      }
      if (firstError) toast.error(firstError)
    } catch (err) {
      if (!shown()) return
      const msg = (err as Error)?.message
      toast.error(msg ? `Paste failed — ${msg}` : 'Paste failed')
    } finally {
      pastingRef.current = false
      setPasting(false)
    }
  }, [filterEligible, queryClient, t, toast, confirm, dest?.isOrg, dest?.tenantName])

  // Ctrl/Cmd+V anywhere on the page (outside text inputs) pastes — only while
  // this deck is on screen: the Projects/MiniApps decks stay mounted hidden, and
  // a window listener would otherwise answer a paste meant for another deck.
  const deckActive = dest?.deckActive ?? true
  useEffect(() => {
    if (eligible.length === 0 || !deckActive) return
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'v' || e.shiftKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      e.preventDefault()
      void paste()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [eligible.length, paste, deckActive])

  return { pastable: eligible.length, pasting, paste }
}
